import type { MessageSuggestion } from "@expert/shared-types";

import { type GaiaWebSocketResponse, gaiaWsEventBus } from "@expert/gaia";
import { trackFullStoryEvent } from "@expert/monitoring";
import { EligibilityStatus, useOfferChecklistStore, useProductOfferStore } from "@expert/sales-tools";
import { useEffect } from "react";

import { getRootDispatcher } from "@/analytics";
import { sendToHoneycomb, toOfferChecklistState } from "@/analytics/sendToHoneycomb";
import { useControlsStore } from "@/call-controls/CallControls/controls.store";

export const useSalesChecklistEventListener = () => {
    useEffect(() => {
        const cleanup = gaiaWsEventBus.on("gaia_ws_message-suggestion", ({ body }: GaiaWebSocketResponse) => {
            const transformed = body as MessageSuggestion;
            if (transformed.suggestion.responseType !== "sales-checklist-mentions") {
                return;
            }

            const selectedProduct = useProductOfferStore.getState().getSelectedProduct(transformed.sessionGroupId);
            const primaryProducts = useProductOfferStore.getState().getPrimaryProducts(transformed.sessionGroupId);
            const currentProduct = selectedProduct ?? primaryProducts.at(0);
            const productInfo = useProductOfferStore
                .getState()
                .getProductInfo(transformed.sessionGroupId, currentProduct?.productSku);

            transformed.suggestion.sales_checklist_mentions?.forEach((mention) => {
                void getRootDispatcher().dispatchBusinessEvent("SalesChecklistBehaviorMentioned", {
                    behavior: mention.name,
                    isEligible: currentProduct?.status === EligibilityStatus.Eligible,
                    isMuted: useControlsStore.getState().isMuted,
                });

                trackFullStoryEvent("SalesChecklistBehaviorMentioned", {
                    behavior: mention.name,
                    productSku: currentProduct?.productSku,
                    sessionId: transformed.sessionGroupId,
                });

                useOfferChecklistStore
                    .getState()
                    .setData(transformed.sessionGroupId, { [mention.name]: mention.value });
            });

            const salesOfferChecklist = productInfo ? productInfo.salesOfferChecklist : undefined;
            const detectedKeys = useOfferChecklistStore.getState().getData(transformed.sessionGroupId);
            sendToHoneycomb(transformed.sessionGroupId, {
                salesOfferChecklist:
                    currentProduct?.status === EligibilityStatus.Eligible
                        ? toOfferChecklistState(detectedKeys, salesOfferChecklist)
                        : undefined,
            });
        });

        return () => cleanup();
    }, []);
};
